import { ChartData, ChartConfig } from './chart.interfaces';

export class ChartCalculationManager {

  /**
   * Calculate Y-axis values based on data range
   * Also considers dataMin/dataMax from deviation bands to ensure they are fully visible
   * @param data Chart data
   * @param axisId Optional axis ID to filter datasets ('y' or 'y1'). If not provided, calculates for all datasets
   */
  calculateYAxisValues(data: ChartData, axisId?: string): number[] {
    if (!data.datasets || data.datasets.length === 0) {
      return [0, 0.25, 0.5, 0.75, 1];
    }

    const allValues = this.getAllChartValues(data, axisId);

    if (allValues.length === 0) {
      return [0, 0.25, 0.5, 0.75, 1];
    }

    const max = Math.max(...allValues);
    const min = Math.min(...allValues);
    const range = max - min;
    const step = range > 0 ? range / 5 : 0.2;

    return Array.from({ length: 6 }, (_, i) => {
      const value = max - (i * step);
      return Math.round(value * 100) / 100;
    });
  }

  /**
   * Get all values from chart data including deviation bands (dataMin/dataMax)
   * This ensures chart bounds include the deviation band area
   * @param data Chart data
   * @param axisId Optional axis ID to filter datasets ('y' or 'y1'). If not provided, includes all datasets
   */
  private getAllChartValues(data: ChartData, axisId?: string): number[] {
    const allValues: number[] = [];

    for (const dataset of data.datasets) {
      // If axisId is specified, only include datasets for that axis
      if (axisId && dataset.yAxisID && dataset.yAxisID !== axisId) {
        continue;
      }

      // Add main data values
      for (const value of dataset.data) {
        if (value !== null) {
          allValues.push(value);
        }
      }

      // Add dataMin values (lower bound of deviation band)
      if (dataset.dataMin) {
        for (const value of dataset.dataMin) {
          if (value !== null) {
            allValues.push(value);
          }
        }
      }

      // Add dataMax values (upper bound of deviation band)
      if (dataset.dataMax) {
        for (const value of dataset.dataMax) {
          if (value !== null) {
            allValues.push(value);
          }
        }
      }
    }

    return allValues;
  }

  /**
   * Get Y position for a value on the chart
   * Uses all values including deviation bands to ensure consistent positioning
   * @param value The value to position
   * @param data Chart data
   * @param config Chart config
   * @param axisId Optional axis ID ('y' or 'y1'). If not provided, uses all data
   */
  getYPosition(value: number, data: ChartData, config: ChartConfig, axisId?: string): number {
    const chartHeight = config.chartHeight ?? 350;
    const topPadding = config.padding?.top ?? 50;

    if (!data.datasets || data.datasets.length === 0) {
      return topPadding + chartHeight;
    }

    const allValues = this.getAllChartValues(data, axisId);

    if (allValues.length === 0) {
      return topPadding + chartHeight;
    }

    const maxValue = Math.max(...allValues);
    const minValue = Math.min(...allValues);
    const range = maxValue - minValue || 1;

    const normalizedValue = (value - minValue) / range;
    return topPadding + chartHeight - (normalizedValue * chartHeight);
  }

  /**
   * Get chart width
   */
  getChartWidth(config: ChartConfig): number {
    return (config.viewBox?.width ?? 800) - (config.padding?.left ?? 50) - (config.padding?.right ?? 50);
  }
}

